import { useCallback } from 'react';
import { useConfig } from '@lore/config';

/**
 * Error thrown for any failed video API call. `status` is the HTTP status, or
 * 0 when the request never reached the server. `serverMessage` is the
 * backend's `{ error }` text when it sent one. `body` is the parsed JSON error
 * body (for extra fields such as a 409's `holder`), or null.
 */
export class ApiError extends Error {
  /**
   * @param {number} status
   * @param {string|null} serverMessage
   * @param {Object|null} [body]
   */
  constructor(status, serverMessage, body = null) {
    super(serverMessage || `API request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.serverMessage = serverMessage || null;
    this.body = body || null;
  }
}

async function readErrorBody(res) {
  try {
    const body = await res.json();
    if (!body || typeof body !== 'object') return { message: null, body: null };
    if (typeof body.error === 'string') return { message: body.error, body };
    if (typeof body.message === 'string') return { message: body.message, body };
    return { message: null, body };
  } catch {
    // Non-JSON error body.
    return { message: null, body: null };
  }
}

/**
 * Low-level JSON request helper for the steward-api. Uses the configured
 * apiRoot (which already includes /community_v3) and auth headers.
 *
 * @param {{ apiRoot: string, headers: Function }} connection
 * @param {string} path - e.g. '/staff/videos' or '/homepage-videos'
 * @param {{ method?: string, body?: Object }} [options]
 * @returns {Promise<any>} Parsed JSON, or null for 204 responses.
 */
export async function apiRequest(connection, path, options = {}) {
  const { method = 'GET', body } = options;
  const hasBody = body !== undefined;

  let res;
  try {
    res = await fetch(`${connection.apiRoot}${path}`, {
      method,
      headers: {
        ...connection.headers(),
        ...(hasBody ? { 'Content-Type': 'application/json' } : {})
      },
      ...(hasBody ? { body: JSON.stringify(body) } : {})
    });
  } catch {
    throw new ApiError(0, null);
  }

  if (!res.ok) {
    const { message, body: errorBody } = await readErrorBody(res);
    throw new ApiError(res.status, message, errorBody);
  }

  if (res.status === 204) {
    return null;
  }

  return res.json();
}

/**
 * Returns a stable `request(path, options)` bound to the default connection.
 *
 * @returns {(path: string, options?: { method?: string, body?: Object }) => Promise<any>}
 */
export function useApiRequest() {
  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;

  return useCallback(
    (path, options) => apiRequest({ apiRoot, headers }, path, options),
    [apiRoot, headers]
  );
}
